/**
 * Pair Calculator — pure client-side derivePairRisk over two leg configs.
 *
 * Two presets seed the inputs (Maven 10k, IF Micro 5k); operator can
 * override any field. Everything below the preset picker is plain
 * controlled inputs; the derivation re-runs on every change.
 */

import { useMemo, useState, type ReactNode } from "react";
import { motion } from "motion/react";
import { AnimateNumber } from "motion-plus/react";
import {
  RISK_PRESETS,
  derivePairRisk,
  type AccountRiskConfig,
  type PairDerivedRisk,
} from "@pfh/shared";
import { CoolIcon } from "@/components/ui/cool-icon";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

type PresetKey = "maven_10k" | "if_micro_5k" | "custom";

const PRESET_LABELS: Record<PresetKey, string> = {
  maven_10k: "Maven 10k",
  if_micro_5k: "IF Micro 5k",
  custom: "Custom",
};

export function Calculator(): ReactNode {
  const [legA, setLegA] = useState<AccountRiskConfig>(RISK_PRESETS.maven_10k);
  const [legB, setLegB] = useState<AccountRiskConfig>(RISK_PRESETS.if_micro_5k);
  const [headroomPct, setHeadroomPct] = useState<number>(30);

  const derived = useMemo<PairDerivedRisk>(
    () =>
      derivePairRisk({
        legA,
        legB,
        slippageHeadroomPct: clamp(headroomPct, 0, 95) / 100,
      }),
    [legA, legB, headroomPct],
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Plug In Two Legs. The Math Re-Runs Live.
        </p>
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="font-mono uppercase tracking-wide">Slippage Headroom</span>
          <Input
            type="number"
            min={0}
            max={95}
            step={1}
            value={headroomPct}
            onChange={(e) => setHeadroomPct(Number(e.target.value) || 0)}
            className="h-8 w-20 font-mono"
          />
          <span className="font-mono">%</span>
        </label>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <LegEditor label="Leg A" value={legA} onChange={setLegA} />
        <LegEditor label="Leg B" value={legB} onChange={setLegB} />
      </div>

      <DerivedPanel derived={derived} />
    </div>
  );
}

function LegEditor({
  label,
  value,
  onChange,
}: {
  label: string;
  value: AccountRiskConfig;
  onChange: (next: AccountRiskConfig) => void;
}): ReactNode {
  const presetMatch = matchPreset(value);

  const handlePreset = (key: PresetKey): void => {
    if (key === "custom") return;
    onChange({ ...RISK_PRESETS[key] });
  };

  const setField = <K extends keyof AccountRiskConfig>(
    key: K,
    raw: number,
  ): void => {
    onChange({ ...value, [key]: raw } as AccountRiskConfig);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
      className="rounded-xl border border-border/60 bg-card/50 p-4"
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-foreground">{label}</h3>
        <select
          value={presetMatch}
          onChange={(e) => handlePreset(e.target.value as PresetKey)}
          className="h-8 rounded-md border border-border/60 bg-background px-2 text-xs"
        >
          {(Object.keys(PRESET_LABELS) as PresetKey[]).map((k) => (
            <option key={k} value={k}>
              {PRESET_LABELS[k]}
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <NumField
          label="Account Size ($)"
          value={value.startingBalance}
          onChange={(v) =>
            onChange({
              ...value,
              startingBalance: v,
              currentBalance: v,
              currentEquity: v,
              startOfDayEquity: v,
            })
          }
        />
        <NumField
          label="Max Floating (%)"
          value={pctToInput(value.maxFloatingPct)}
          onChange={(v) => setField("maxFloatingPct", v / 100)}
          allowNull
          isNull={value.maxFloatingPct == null}
          onNull={() => setField("maxFloatingPct", null as unknown as number)}
        />
        <NumField
          label="Daily DD (%)"
          value={value.dailyDdPct * 100}
          onChange={(v) => setField("dailyDdPct", v / 100)}
        />
        <NumField
          label="Lifetime DD (%)"
          value={value.lifetimeDdPct * 100}
          onChange={(v) => setField("lifetimeDdPct", v / 100)}
        />
        <NumField
          label="Consistency (%)"
          value={pctToInput(value.consistencyPct)}
          onChange={(v) => setField("consistencyPct", v / 100)}
          allowNull
          isNull={value.consistencyPct == null}
          onNull={() => setField("consistencyPct", null as unknown as number)}
        />
        <div /> {/* spacer to keep grid aligned */}
      </div>
    </motion.div>
  );
}

function DerivedPanel({ derived }: { derived: PairDerivedRisk }): ReactNode {
  const bindingLabel =
    derived.bindingLeg === "tie"
      ? "Both Legs Tie"
      : `Leg ${derived.bindingLeg} Binds`;
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1], delay: 0.05 }}
      className="rounded-xl border border-accent/30 bg-accent/5 p-4"
    >
      <div className="mb-3 flex items-center gap-2">
        <CoolIcon name="Calculator" size={16} className="text-accent" />
        <h3 className="text-sm font-semibold text-foreground">Derived</h3>
        <span className="ml-auto rounded-full border border-accent/30 bg-accent/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wide text-accent">
          {bindingLabel}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <DerivedTile
          label="Per-Trade SL"
          value={derived.perTradeSlMaxUsd}
          format="usd"
          accent
        />
        <DerivedTile
          label="Daily Target"
          value={isFiniteNumber(derived.dailyTargetUsd) ? derived.dailyTargetUsd : null}
          format="usd"
        />
        <DerivedTile
          label="Days To Withdraw"
          value={derived.daysToFirstDualWithdrawal || null}
          format="int"
          suffix=" days"
        />
        <DerivedTile
          label="Lifetime SL Hits"
          value={derived.lifetimeSlHitBudget}
          format="int"
          suffix=" hits"
        />
      </div>

      {derived.notes.length > 0 && (
        <ul className="mt-4 flex flex-col gap-1.5 border-t border-border/40 pt-3 text-[12px] text-muted-foreground">
          {derived.notes.map((note, i) => (
            <li key={i} className="flex items-start gap-2">
              <CoolIcon name="Info" size={12} className="mt-0.5 shrink-0 text-accent" />
              <span>{note}</span>
            </li>
          ))}
        </ul>
      )}
    </motion.div>
  );
}

function DerivedTile({
  label,
  value,
  format,
  suffix,
  accent,
}: {
  label: string;
  value: number | null;
  format: "usd" | "int";
  suffix?: string;
  accent?: boolean;
}): ReactNode {
  const display = value == null || !isFiniteNumber(value);
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span
        className={cn(
          "font-mono text-2xl font-semibold tabular-nums",
          accent ? "text-accent" : "text-foreground",
        )}
      >
        {display ? (
          "—"
        ) : format === "usd" ? (
          <>
            $
            <AnimateNumber
              format={{ maximumFractionDigits: 0 }}
            >
              {value as number}
            </AnimateNumber>
          </>
        ) : (
          <>
            <AnimateNumber>{value as number}</AnimateNumber>
            {suffix}
          </>
        )}
      </span>
    </div>
  );
}

function NumField({
  label,
  value,
  onChange,
  allowNull,
  isNull,
  onNull,
}: {
  label: string;
  value: number;
  onChange: (next: number) => void;
  allowNull?: boolean;
  isNull?: boolean;
  onNull?: () => void;
}): ReactNode {
  return (
    <label className="flex flex-col gap-1 text-xs">
      <span className="flex items-center justify-between text-muted-foreground">
        <span>{label}</span>
        {allowNull && (
          <button
            type="button"
            onClick={() => onNull?.()}
            className="text-[10px] uppercase tracking-wide text-muted-foreground hover:text-accent"
          >
            {isNull ? "None" : "Set None"}
          </button>
        )}
      </span>
      <Input
        type="number"
        step="any"
        value={isNull ? "" : Number.isFinite(value) ? value : ""}
        placeholder={isNull ? "—" : undefined}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === "") return;
          onChange(Number(raw));
        }}
        className="h-8 font-mono"
      />
    </label>
  );
}

function pctToInput(v: number | null): number {
  if (v == null) return 0;
  return v * 100;
}

function clamp(v: number, lo: number, hi: number): number {
  if (!Number.isFinite(v)) return lo;
  return Math.max(lo, Math.min(hi, v));
}

function isFiniteNumber(v: unknown): v is number {
  return typeof v === "number" && Number.isFinite(v);
}

function matchPreset(value: AccountRiskConfig): PresetKey {
  if (configMatches(value, RISK_PRESETS.maven_10k)) return "maven_10k";
  if (configMatches(value, RISK_PRESETS.if_micro_5k)) return "if_micro_5k";
  return "custom";
}

function configMatches(a: AccountRiskConfig, b: AccountRiskConfig): boolean {
  return (
    a.startingBalance === b.startingBalance &&
    a.maxFloatingPct === b.maxFloatingPct &&
    a.dailyDdPct === b.dailyDdPct &&
    a.lifetimeDdPct === b.lifetimeDdPct &&
    a.consistencyPct === b.consistencyPct
  );
}
